/**
 * Progress Bar Component for Blog Posts
 * 
 * Displays a vertical progress bar on the left side showing:
 * - Reading progress (gray-900 fill)
 * - Section markers (gray dots, darker when active)
 * - Click markers to navigate to sections
 * - Updates URL hash for sharing specific sections
 */

"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";

interface Section {
  id: string;
  title: string;
  offsetTop: number;
}

interface ProgressBarProps {
  headings: Array<{ content: string; index: number }>;
}

export default function ProgressBar({ headings }: ProgressBarProps) {
  const router = useRouter();
  const [progress, setProgress] = useState(0);
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Generate section IDs and find elements
  useEffect(() => {
    // Wait for DOM to be fully ready
    const findSections = () => {
      const sectionElements: Section[] = [];
      
      headings.forEach((heading) => {
        const id = `section-${heading.index}`;
        const element = document.getElementById(id);
        
        if (element) {
          sectionElements.push({
            id,
            title: heading.content,
            offsetTop: element.offsetTop,
          });
        }
      });

      if (sectionElements.length > 0) {
        setSections(sectionElements);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      findSections();
    });

    // Navigate to hash on load if present
    if (typeof window !== 'undefined' && window.location.hash) {
      const element = document.getElementById(window.location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [headings]);

  // Calculate scroll progress and active section
  const handleScroll = useCallback(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    
    // Calculate progress (0-100)
    const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;
    setProgress(Math.min(100, Math.max(0, scrollPercent)));

    // Determine active section
    if (sections.length > 0) {
      const currentScroll = scrollTop + windowHeight / 3; // Trigger earlier
      
      for (let i = sections.length - 1; i >= 0; i--) {
        if (currentScroll >= sections[i].offsetTop) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    }
  }, [sections]);

  // Attach scroll listener
  useEffect(() => {
    handleScroll(); // Initial calculation
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  // Navigate to section
  const scrollToSection = (sectionId: string) => {
    if (typeof window === 'undefined') return;
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      
      // Update URL hash
      const newUrl = `${window.location.pathname}${window.location.search}#${sectionId}`;
      router.push(newUrl, { scroll: false });
    }
  };

  // Calculate marker positions
  const getMarkerPosition = (section: Section): number => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
    
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const scrollableHeight = documentHeight - windowHeight;
    
    if (scrollableHeight <= 0) return 0;
    
    return Math.min(100, (section.offsetTop / scrollableHeight) * 100);
  };

  return (
    <div className="fixed left-0 top-8 h-screen w-6 xl:w-12 items-center justify-center pointer-events-none z-40 hidden lg:flex">
      <div className="relative h-[calc(100vh-8rem)] w-1 bg-gray-200 rounded overflow-hidden pointer-events-auto">
        {/* Progress Fill */}
        <div
          className="absolute top-0 left-0 w-full bg-gray-900 transition-all duration-150 ease-out rounded"
          style={{ height: `${progress}%` }}
        />

        {/* Section Markers */}
        {sections.map((section) => {
          const markerPos = getMarkerPosition(section);
          const isActive = activeSection === section.id;
          
          return (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="absolute left-1/2 -translate-x-1/2 group"
              style={{ top: `${markerPos}%` }}
              title={section.title}
              aria-label={`Jump to: ${section.title}`}
            >
              {/* Marker Dot */}
              <div
                className={`w-8 h-3 rounded-full transition-all duration-200 ${
                  isActive
                    ? "bg-gray-900 ring-4 ring-white"
                    : "bg-gray-600 hover:bg-gray-900"
                }`}
              />
              
              {/* Tooltip */}
              <div className="absolute left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                <div className="bg-gray-900 text-white px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap font-poppins text-xs">
                  {section.title}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

